import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { EASE } from "@/components/Reveal";
import VlrLogo from "@/components/VlrLogo";

export default function Header() {
  const { t, lang, setLang, localizePath } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const links = [
    { to: "/#products", label: t("nav.products"), id: "products" },
    { to: "/#standard", label: t("nav.standard"), id: "standard" },
    { to: "/#professionals", label: t("nav.professionals"), id: "professionals" },
    { to: "/enterprise", label: t("nav.enterprise"), id: "enterprise" },
    { to: "/press", label: t("nav.press"), id: "press" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "border-b border-ink/10 bg-paper/85 backdrop-blur-md"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 md:h-[72px] md:px-10">
        <Link
          to={localizePath("/")}
          data-testid="site-logo"
          className="group flex items-center gap-2.5"
          aria-label="VLR Software LLC — home"
        >
          <VlrLogo className="h-8 w-8 text-ink transition-transform duration-500 group-hover:scale-105" />
          <span className="text-[15px] font-semibold tracking-tight">VLR Software LLC</span>
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          <nav className="flex items-center gap-8" aria-label="Primary">
            {links.map((l) => (
              <Link
                key={l.id}
                to={localizePath(l.to)}
                data-testid={`nav-link-${l.id}`}
                className="group relative text-[13px] font-medium tracking-wide text-ink/70 transition-colors duration-300 hover:text-ink"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-cobalt transition-[width] duration-300 group-hover:w-full" />
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 border-l border-ink/10 pl-6" data-testid="header-language-toggle">
            {["en", "es"].map((code) => (
              <button
                key={code}
                type="button"
                data-testid={`header-lang-toggle-${code}`}
                aria-pressed={lang === code}
                onClick={() => setLang(code)}
                className={`border-b pb-0.5 text-[12px] font-semibold uppercase tracking-wider transition-colors ${
                  lang === code ? "border-cobalt text-ink" : "border-transparent text-ink/40 hover:text-ink"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? t("nav.close") : t("nav.menu")}
          className="flex h-10 w-10 items-center justify-center md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="border-t border-ink/10 bg-paper px-5 pb-8 pt-4 md:hidden"
            aria-label="Mobile"
          >
            {links.map((l, i) => (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.4, ease: EASE }}
              >
                <Link
                  to={localizePath(l.to)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className="block border-b border-ink/5 py-4 font-serif text-3xl text-ink transition-colors hover:text-cobalt"
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
            <div className="mt-6 flex items-center gap-4" data-testid="mobile-language-toggle">
              <span className="text-[11px] uppercase tracking-[0.25em] text-ink/40">{t("footer.language")}</span>
              {["en", "es"].map((code) => (
                <button
                  key={code}
                  type="button"
                  data-testid={`mobile-lang-toggle-${code}`}
                  aria-pressed={lang === code}
                  onClick={() => setLang(code)}
                  className={`border-b pb-0.5 text-sm font-medium uppercase tracking-wider transition-colors ${
                    lang === code ? "border-cobalt text-ink" : "border-transparent text-ink/40 hover:text-ink"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
